import React, { useState } from 'react';

/**
 * Utils
 */
import { shouldEmployeeSendReport, getCurrentUserStaffID } from '../../utils/staff';

/**
 * Contexts
*/
import { useContext } from 'react';
import { AppContext } from '../../contexts/AppContext';

/**
 * MUI Compoments
 */
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';

/**
 * MUI Icons
 */
import RunningWithErrorsIcon from '@mui/icons-material/RunningWithErrors';
import LockClockIcon from '@mui/icons-material/LockClock';

/**
 * Components
 */
import WorkBasicInfoModal from './WorkBasicInfoModal';
import PlanningReportCount from './PlanningReportCount';

/**
 * Hooks
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ViewPrlDocumentsModal from '../PrlDocument/ViewPrlDocumentsModal';
import ViewWorkDocumentsModal from '../WorkDocument/ViewWorkDocumentsModal';

const styles = {
    cardBG: {
        backgroundSize: "100% 100%",
        backgroundPosition: "0px 0px",
        backgroundImage: "linear-gradient(90deg, #FFF 0%, #B3111721 100%)",
    },
    cardLocked: {
        backgroundSize: "100% 100%",
        backgroundPosition: "0px 0px",
        backgroundImage: "linear-gradient(90deg, #FFF 0%,rgba(101, 117, 141, 0.55) 100%)",
    },
    cardOlderThanToday: {
        backgroundSize: "100% 100%",
        backgroundPosition: "0px 0px",
        backgroundImage: "linear-gradient(90deg, #FFF 0%,rgba(247, 111, 44, 0.94) 100%)",
    },
};

/**
 * Work row card
 * @component
 * @param {Object} planning the planning
 * @param {boolean} lock the lock state
 */
export default function WorkRowCard({ planning, lock = false }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const appData = useContext(AppContext);
    const [open, setOpen] = useState(false);

    // PRL Documents
    const [showDocuments, setShowDocuments] = useState(false);
    const handleOpenPRLDocuments = e => {
        e.preventDefault();
        e.stopPropagation();
        setShowDocuments(true);
    };
    const handleClosePRLDocuments = () => {
        setShowDocuments(false);
    };

    // Work Documents
    const [showWorkDocuments, setShowWorkDocuments] = useState(false);
    const handleOpenWorkDocuments = e => {
        e.preventDefault();
        e.stopPropagation();
        setShowWorkDocuments(true);
    };
    const handleCloseWorkDocuments = () => {
        setShowWorkDocuments(false);
    };

    const handleShow = e => {
        e.preventDefault();
        e.stopPropagation();

        // Data to be passed
        const dynamicData = {
            uid: appData.userInfo?.id,
            planning,
            work: planning?.work,
        };

        navigate('/work-report', { state: dynamicData });
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const MyCardActions = () => {
        const sid = getCurrentUserStaffID(appData);
        const disablePRL = !(Array.isArray(planning.work.prl_documents) && planning.work.prl_documents.length);
        const disableWorkDocs = !(Array.isArray(planning.work.work_documents) && planning.work.work_documents.length);
        const disableSendReport = !shouldEmployeeSendReport(sid, planning);

        return (
            <>
                <Button size="small" onClick={handleOpenPRLDocuments} disabled={disablePRL}>
                    {t('PRL')}
                </Button>
                <Button size="small" onClick={handleOpenWorkDocuments} disabled={disableWorkDocs}>
                    {t('Documents')}
                </Button>
                {!lock ? (
                    <Button size="small" color="primary" onClick={handleShow} disabled={disableSendReport}>
                        {t('Send report')}
                    </Button>
                ) : null}
            </>
        );
    };

    const WorkLocationInfo = () => {
        const aliasLocation = planning.location || '';
        const { locality, municipality } = planning.work.work_location || {};

        return (
            <>
                {aliasLocation?.trim() ? (
                    <Typography variant="caption" color="text.secondary" component="h6">
                        <strong>{aliasLocation}</strong>
                    </Typography>
                ) : null}
                {(locality?.trim() || municipality?.trim()) ? (
                    <Typography variant="caption" color="text.secondary" component="div">
                        {locality} | {municipality}
                    </Typography>
                ) : null}
            </>
        );
    };

    const WorkShiftInfo = () => {
        const shift = planning.shift || '';

        if (!shift) {
            return null;
        }

        return (
            <>
                <Typography variant="caption" color="text.secondary" component="div">
                    <strong>{t('Shift')}: <Chip label={shift} size="small" color="primary" /></strong>
                </Typography>
            </>
        );
    };

    const WorkTaskInfo = () => {
        const task = planning.task || '';

        if (!task) {
            return null;
        }

        return (
            <>
                <Typography variant="caption" color="text.secondary" component="div">
                    <strong>{t('Task')}: <Chip label={task} size="small" color="primary" /></strong>
                </Typography>
            </>
        );
    };

    const WorkGroupInfo = () => {

        let hasStaff = planning.staff && planning.staff.length > 0;

        return (
            <>
                <Typography variant="caption" color="text.secondary" component="div">
                    <strong>{t('Work group')} {planning.group || t('Unknown work group')}</strong>
                    <br />

                    {/* If has responsable show it */}
                    {planning.responsable && (
                        <>
                            <strong>
                                {t('Responsable')}: {planning.responsable.full_name || t('Unknown staff member')}
                            </strong>
                            <br />
                        </>
                    )}


                    {/* List the employees group members */}
                    {hasStaff && (
                        <>
                            <strong>{t('Group members')}</strong>
                            <br />
                            <ul>
                                {planning?.staff?.map((staff, index) => (
                                    <li key={index}>
                                        {staff?.full_name || t('Unknown staff member')}
                                    </li>
                                )) || null}
                            </ul>
                        </>
                    )}

                </Typography>
            </>
        );
    };

    const WorkVehiclesInfo = () => {
        return (planning.vehicles && planning.vehicles.length > 0) && (
            <>
                <Typography variant="caption" color="text.secondary" component="div">
                    <strong>{t('Vehicles')}</strong>
                    <br />
                    <ul>
                        {planning.vehicles.map((vehicle, index) => (
                            <li key={index}>
                                {vehicle.code}
                                {vehicle.denomination}
                            </li>
                        ))}
                    </ul>
                </Typography>
            </>
        );
    };

    const WorkTrailersInfo = () => {
        return (planning.trailers && planning.trailers.length > 0) && (
            <>
                <Typography variant="caption" color="text.secondary" component="div">
                    <strong>{t('Trailers')}</strong>
                    <br />
                    <ul>
                        {planning.trailers.map((trailer, index) => (
                            <li key={index}>
                                {trailer.code}
                                {trailer.denomination}
                            </li>
                        ))}
                    </ul>
                </Typography>
            </>
        );
    };

    // Date of the planning
    let theDate = planning.date || '';
    if (theDate) {
        const date = new Date(theDate);
        const day = date.getDate();
        const month = date.toLocaleString('es', { month: 'long' });
        const year = date.getFullYear();
        theDate = `${day} ${month} ${year}`;
    }

    const isOlderThanToday = () => {
        const today = new Date().toISOString().split('T')[0];
        const planningDate = planning.date.slice(0, 10);
        return planningDate < today;
    };

    const getSectorName = () => {
        const sector = planning?.work?.work_action_areas.find(area => area.parent_id === null);
        return sector?.name || '';
    };

    const subtitle = () => {
        const sectorName = getSectorName();

        if (sectorName) {
            return `${sectorName}`;
            // return `${planning.work.kind_name} | ${planning.work.work_type_name} | ${sectorName}`;
        }

        return '';
        // return `${planning.work.kind_name} | ${planning.work.work_type_name}`;
    };

    const cardStyle = () => {
        if (isOlderThanToday()) {
            return styles.cardOlderThanToday;
        }
        return lock ? styles.cardLocked : styles.cardBG;
    };

    return (
        <>
            <Card
                sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}
                style={cardStyle()}
                onClick={handleOpen}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <CardContent sx={{ flex: '1 0 auto' }}>
                        <Typography
                            component="div"
                            variant="h5"
                            sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                            <div>{planning.work.alias || planning.work.name}</div>
                            <Box sx={{ color: 'text.secondary', display: 'flex', gap: 1, alignItems: 'center' }}>
                                {lock ? (
                                    <LockClockIcon />
                                ) : null}{theDate}
                                {isOlderThanToday() ? (
                                    <RunningWithErrorsIcon />
                                ) : null}
                            </Box>
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary" component="div">
                            {subtitle()}
                        </Typography>
                        <WorkLocationInfo />
                        <WorkShiftInfo />
                        <WorkTaskInfo />
                        <Box sx={{
                            display: 'flex',
                            gap: { xs: 2, sm: 4 },
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between'
                        }}>
                            <Box id="work-group-info" sx={{ flex: 1 }}>
                                <WorkGroupInfo />
                            </Box>
                            <Box id="work-vehicles-info" sx={{ flex: 1 }}>
                                <WorkVehiclesInfo />
                                <WorkTrailersInfo />
                            </Box>
                        </Box>
                    </CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', pl: 1, pb: 1 }}>
                        <MyCardActions />
                        <PlanningReportCount planning={planning} />
                    </Box>
                </Box>
            </Card>

            <WorkBasicInfoModal
                open={open}
                handleClose={handleClose}
                planning={planning}
            />

            <ViewPrlDocumentsModal open={showDocuments}
                handleClose={handleClosePRLDocuments}
                documents={planning.work.prl_documents}
                workId={planning.work.id}
            />

            <ViewWorkDocumentsModal
                open={showWorkDocuments}
                handleClose={handleCloseWorkDocuments}
                documents={planning.work.work_documents || []}
            />
        </>
    );
}